import { useCallback, useEffect, useRef } from "react"
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { useSelector as useSelectorBase } from "react-redux"
import type prefsSlice from "./prefsSlice"
import { getAutosave } from "./prefsSlice"

// Any store that renders forms includes prefsSlice; this hook needs no more of it
const useSelector = useSelectorBase.withTypes<{
  prefs: ReturnType<typeof prefsSlice.reducer>
}>()

const AUTOSAVE_DELAY = Math.E * 1000

export default (callback: () => Promise<unknown>, isDirty: boolean) => {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const autosave = useSelector(getAutosave)
  const isDirtyRef = useRef(isDirty)
  const autosaveRef = useRef(autosave)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSavingRef = useRef(false)

  useEffect(() => {
    isDirtyRef.current = isDirty
  }, [isDirty])
  useEffect(() => {
    autosaveRef.current = autosave
  }, [autosave])

  // Stable function – reads everything from refs, no deps.
  // Callers invoke this on each edit for true debounce (timer restarts).
  // After a successful save it self-re-arms, so mid-save edits are picked up.
  const restart = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (!autosaveRef.current || !isDirtyRef.current || isSavingRef.current) {
      return
    }

    timerRef.current = setTimeout(() => {
      isSavingRef.current = true
      void callbackRef.current().then(
        () => {
          isSavingRef.current = false
          restart() // Re-arm if still dirty after save
        },
        () => {
          isSavingRef.current = false
        },
      )
    }, AUTOSAVE_DELAY)
  }, [])

  // Start when dirty, stop when clean
  useEffect(() => {
    if (isDirty && autosave) {
      restart()
    } else if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isDirty, autosave, restart])

  // Cancel any pending autosave timer. Callers invoke this before manual saves
  // to prevent the armed timer from firing a duplicate save request.
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  return { isEnabled: autosave, restart, cancel }
}
